/**
 * WorkerScheduleCalendar.js - Calendar view of upcoming jobs with co-workers
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Users, Clock, 
  MapPin, DollarSign, Car, Home, Package, Truck, Shirt, RefreshCw
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import api from '../../services/api';

const SERVICE_ICONS = {
  rides: Car, ride: Car, cleaning: Home, laundry: Shirt, courier: Truck, errands: Package
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December'];

const WorkerScheduleCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      // Get worker's scheduled jobs for the month
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const res = await api.get('/schedules/worker', {
        params: {
          start_date: startOfMonth.toISOString().split('T')[0],
          end_date: endOfMonth.toISOString().split('T')[0]
        }
      });
      setJobs(res.data?.schedules || res.data || []);
    } catch (error) {
      console.log('Failed to fetch schedule:', error);
      // Try alternate endpoint
      try {
        const res = await api.get('/bookings/my-jobs', {
          params: { status: 'confirmed,assigned,in_progress' }
        });
        setJobs(res.data?.bookings || res.data || []);
      } catch (e) {
        console.log('Fallback also failed:', e);
      }
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentDate);

  const getJobsForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return jobs.filter(job => {
      const jobDate = job.scheduled_date || job.date || job.booking_date;
      return jobDate?.startsWith(dateStr);
    });
  };

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));

  const today = new Date();
  const isToday = (day) => 
    day === today.getDate() && 
    currentDate.getMonth() === today.getMonth() && 
    currentDate.getFullYear() === today.getFullYear();

  const selectedDateJobs = getJobsForDate(selectedDate.getDate());

  return (
    <div className="bg-[var(--card)] rounded-xl border border-[var(--border)] overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-[var(--border)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 text-[var(--primary)]" />
          <h3 className="font-semibold text-[var(--text-primary)]">My Schedule</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={prevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium min-w-[140px] text-center">
            {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
          </span>
          <Button variant="ghost" size="icon" onClick={nextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAYS.map(day => (
            <div key={day} className="text-center text-xs font-medium text-[var(--text-muted)] py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Days */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for days before start of month */}
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} className="h-12" />
          ))}
          
          {/* Days of month */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dayJobs = getJobsForDate(day);
            const hasJobs = dayJobs.length > 0;
            const hasTeamJob = dayJobs.some(j => (j.workers_needed || 1) > 1);
            const isSelected = selectedDate.getDate() === day && 
                             selectedDate.getMonth() === currentDate.getMonth();
            
            return (
              <motion.button
                key={day}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))}
                className={`h-12 rounded-lg relative flex flex-col items-center justify-center transition-colors ${
                  isSelected 
                    ? 'bg-[var(--primary)] text-white' 
                    : isToday(day) 
                      ? 'bg-[var(--primary)]/20 text-[var(--primary)]'
                      : hasJobs 
                        ? 'bg-blue-500/10 hover:bg-blue-500/20' 
                        : 'hover:bg-[var(--accent)]'
                }`}
              >
                <span className={`text-sm ${isSelected ? 'font-bold' : ''}`}>{day}</span>
                {hasJobs && (
                  <div className="flex gap-0.5 mt-0.5">
                    {dayJobs.slice(0, 3).map((_, idx) => (
                      <div 
                        key={idx} 
                        className={`w-1.5 h-1.5 rounded-full ${
                          isSelected ? 'bg-white' : hasTeamJob ? 'bg-amber-400' : 'bg-blue-400'
                        }`} 
                      />
                    ))}
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Selected Day Jobs */}
      <div className="p-4 border-t border-[var(--border)]">
        <h4 className="font-medium text-[var(--text-primary)] mb-3">
          {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </h4>
        
        {loading ? (
          <div className="text-center py-4 text-[var(--text-muted)]">
            <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
            Loading...
          </div>
        ) : selectedDateJobs.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)] py-4 text-center">No jobs scheduled</p>
        ) : (
          <div className="space-y-3">
            {selectedDateJobs.map(job => {
              const IconComponent = SERVICE_ICONS[job.service_type] || Package;
              const workersNeeded = job.workers_needed || 1;
              const coworkers = job.workers?.filter(w => w.id !== job.worker_id) || [];
              
              return (
                <div 
                  key={job.id} 
                  className="p-3 rounded-lg bg-[var(--background)] border border-[var(--border)]"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[var(--primary)]/20 flex items-center justify-center">
                      <IconComponent className="h-5 w-5 text-[var(--primary)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[var(--text-primary)] truncate">
                          {job.service_name || job.service_type || 'Job'}
                        </span>
                        {workersNeeded > 1 && (
                          <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                            <Users className="h-3 w-3 mr-1" />
                            Team
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-[var(--text-muted)]">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {job.scheduled_time || job.start_time || 'TBD'}
                        </span>
                        <span className="flex items-center gap-1 text-green-400">
                          <DollarSign className="h-3.5 w-3.5" />
                          {(job.worker_pay || job.total_amount || 0).toFixed(0)}
                        </span>
                      </div>
                      {coworkers.length > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <Users className="h-3.5 w-3.5 text-[var(--text-muted)]" />
                          <span className="text-xs text-[var(--text-muted)]">
                            With: {coworkers.map(w => w.name).join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerScheduleCalendar;
